// Workspace-membership refusals from the backend (tenant segregation).
//
// d2auth proves who someone is; the workspace decides whether they belong. When
// it says no, the response carries one of these codes. All of them mean the
// session cannot be used in this workspace — mid-session they end it, at
// sign-in they are shown as the reason — except DIRECTORY_UNAVAILABLE, which is
// a temporary failure to decide (sign-in only; retrying later can succeed).

export interface MembershipDenial {
  code: string;
  title: string;
  message: string;
  /** False only for a temporary failure: keep the session, let them retry. */
  endsSession: boolean;
}

const DENIALS: Record<string, Omit<MembershipDenial, 'code'>> = {
  USER_NOT_PROVISIONED: {
    title: 'Not a member of this workspace',
    message: 'You are not a member of this workspace. Ask a workspace administrator to add or invite you.',
    endsSession: true,
  },
  USER_INACTIVE: {
    title: 'Account inactive',
    message: 'Your account is inactive. Please contact your administrator.',
    endsSession: true,
  },
  USER_SUSPENDED: {
    title: 'Account suspended',
    message: 'Your account is suspended in this workspace. Please contact a workspace administrator.',
    endsSession: true,
  },
  USER_NOT_IN_DIRECTORY: {
    title: 'No longer in the directory',
    message: 'Your organisation directory no longer lists you for this workspace. Please contact your administrator.',
    endsSession: true,
  },
  TENANT_ORG_MISMATCH: {
    title: 'Wrong organisation',
    message: 'This workspace belongs to a different organisation than the account you signed in with. Sign in with your organisation account.',
    endsSession: true,
  },
  TENANT_SUSPENDED: {
    title: 'Workspace suspended',
    message: 'This workspace is suspended. Contact your administrator.',
    endsSession: true,
  },
  DIRECTORY_UNAVAILABLE: {
    title: 'Membership could not be checked',
    message: 'Your workspace membership could not be verified right now. Please try again in a few minutes.',
    endsSession: false,
  },
};

/**
 * The membership refusal in an API error, or null if it is something else.
 * The server's own sentence wins over the fallback text.
 */
export function membershipDenial(err: unknown): MembershipDenial | null {
  const e = err as { response?: { status?: number; data?: { code?: string; error?: string } } } | undefined;
  const status = e?.response?.status;
  const code = e?.response?.data?.code;
  if (!code || !(code in DENIALS)) return null;
  if (status !== 403 && status !== 503) return null;
  const d = DENIALS[code];
  return { code, ...d, message: e?.response?.data?.error || d.message };
}
